// Invitation and password-reset links (first login; BD-73). The only link that signs anyone in is the project's own:
//   /welcome?token_hash=...&type=invite (or type=recovery), checked here once with the sign-in service (verifyOtp).
// A link that carries session tokens in the address bar (#access_token=...&refresh_token=..., or the same in the query
// string) is never used to sign in: anyone can make such a link with their own login and send it, and whoever opens it
// would then be working in the sender's login without noticing (security review SR1-24, Decision 150 #2c). Such tokens are
// only taken out of the address bar. A link that carries an error (#error=...&error_code=otp_expired, when it had expired
// or was already used) is reported.
// The link is read once and removed from the address bar, so it is never kept in history or bookmarks.

export type LinkType = "invite" | "recovery" | "magiclink" | "signup" | "email";
export type EmailLink =
  | { kind: "check"; tokenHash: string; type: LinkType }
  | { kind: "problem"; code: string };

const TYPES: LinkType[] = ["invite", "recovery", "magiclink", "signup", "email"];
const asType = (t: string | null): LinkType | null => (t && (TYPES as string[]).includes(t) ? (t as LinkType) : null);

const parts = (href: string) => {
  const url = new URL(href);
  return { q: url.searchParams, h: new URLSearchParams(url.hash.replace(/^#/, "")) };
};

export function readEmailLink(href: string): EmailLink | null {
  const { q, h } = parts(href);
  const errorCode = h.get("error_code") ?? q.get("error_code") ?? h.get("error") ?? q.get("error");
  if (errorCode) return { kind: "problem", code: errorCode };
  const tokenHash = q.get("token_hash");
  const qType = asType(q.get("type"));
  if (tokenHash && qType) return { kind: "check", tokenHash, type: qType };
  return null;
}

/** Whether the address carries session tokens (SR1-24): never used, only taken out of the address bar. */
export function carriesSessionTokens(href: string): boolean {
  const { q, h } = parts(href);
  return ["access_token", "refresh_token", "provider_token"].some((k) => h.has(k) || q.has(k));
}

/** Takes the link out of the address bar and opens the app at its start. */
export function clearEmailLink(): void {
  window.history.replaceState(null, "", "/");
}

/** Whether a link of this type asks the person to set up how they sign in (a first login or a reset). */
export const needsSetUp = (t: LinkType): boolean => t === "invite" || t === "recovery" || t === "signup";
